import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Star, Trash2 } from 'lucide-react';
import {
  createGuestContactChannel,
  updateGuestContactChannel,
  deleteGuestContactChannel,
} from '@/api/generated/sdk.gen';
import type { CreateGuestContactChannelRequest } from '@/api/generated/types.gen';
import { guestContactChannelsQuery, type GuestContactChannel } from '@/lib/guests';
import { errorMessage } from '@/lib/errors';
import { useToast } from '@/components/ToastProvider';
import { useConfirm } from '@/components/DialogProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';

type Kind = GuestContactChannel['kind'];

const KIND_OPTIONS: { label: string; value: Kind }[] = [
  { label: 'Phone', value: 'phone' },
  { label: 'WhatsApp', value: 'whatsapp' },
  { label: 'Telegram', value: 'telegram' },
  { label: 'Email', value: 'email' },
  { label: 'Other', value: 'other' },
];
const KIND_LABEL: Record<Kind, string> = {
  phone: 'Phone',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  email: 'Email',
  other: 'Other',
};

// GuestContactChannels — list + add + delete + set-preferred for a guest's
// N contact channels (G3 backend). Inline value/label editing is a follow-up;
// for now correct a channel by deleting and re-adding.
export function GuestContactChannels({ guestId }: { guestId: string }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const { data: channels, isLoading, isError, error } = useQuery(guestContactChannelsQuery(guestId));

  const [kind, setKind] = useState<Kind>('phone');
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [preferred, setPreferred] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['guest-contact-channels', guestId] }).catch(() => undefined);

  const addMut = useMutation({
    // Take the form snapshot as the mutate() argument (like prefer/delete below)
    // rather than closing over state — no stale-closure risk on fast input.
    mutationFn: (body: CreateGuestContactChannelRequest) =>
      createGuestContactChannel({ throwOnError: true, path: { id: guestId }, body }),
    onSuccess: () => {
      setValue('');
      setLabel('');
      setPreferred(false);
      setFormError(null);
      invalidate();
      toast({ kind: 'success', title: 'Channel added' });
    },
    onError: (e) => setFormError(errorMessage(e)),
  });

  const delMut = useMutation({
    mutationFn: (channelId: string) =>
      deleteGuestContactChannel({ throwOnError: true, path: { id: guestId, channel_id: channelId } }),
    onSuccess: () => {
      invalidate();
      toast({ kind: 'success', title: 'Channel removed' });
    },
    onError: (e) => toast({ kind: 'error', title: 'Could not remove channel', message: errorMessage(e) }),
  });

  const preferMut = useMutation({
    // The PATCH is a partial overlay server-side (read-modify-write): sending the
    // existing value + (optional) label preserves them and only flips is_preferred;
    // the backend clears the prior preferred for this kind in the same tx.
    mutationFn: (ch: GuestContactChannel) =>
      updateGuestContactChannel({
        throwOnError: true,
        path: { id: guestId, channel_id: ch.id },
        body: { value: ch.value, label: ch.label, is_preferred: true },
      }),
    onSuccess: () => {
      invalidate();
      toast({ kind: 'success', title: 'Preferred channel updated' });
    },
    onError: (e) => toast({ kind: 'error', title: 'Could not set preferred', message: errorMessage(e) }),
  });

  const onAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!value.trim()) {
      setFormError('Value is required.');
      return;
    }
    addMut.mutate({ kind, value: value.trim(), label: label.trim() || undefined, is_preferred: preferred });
  };

  const onDelete = async (ch: GuestContactChannel) => {
    const ok = await askConfirm({
      title: 'Remove contact channel?',
      body: `${KIND_LABEL[ch.kind]}: ${ch.value}`,
      confirmLabel: 'Remove',
    });
    if (ok) delMut.mutate(ch.id);
  };

  return (
    <section className="mb-6 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.06em] text-muted">Contact channels</h2>

      {isLoading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : isError ? (
        <p className="text-sm text-destructive">{errorMessage(error)}</p>
      ) : !channels || channels.length === 0 ? (
        <p className="text-sm text-muted">No contact channels yet.</p>
      ) : (
        <ul className="mb-4 divide-y divide-[var(--border-hairline)]">
          {channels.map((ch) => (
            <li key={ch.id} className="flex items-center justify-between gap-3 py-2 text-sm">
              <div className="flex min-w-0 items-center gap-2">
                <Badge variant="neutral" size="sm">
                  {KIND_LABEL[ch.kind]}
                </Badge>
                <span className="truncate font-medium">{ch.value}</span>
                {ch.label && <span className="truncate text-xs text-muted">({ch.label})</span>}
                {ch.is_preferred && (
                  <Badge variant="gold" size="sm" icon={<Star aria-hidden="true" />}>
                    Preferred
                  </Badge>
                )}
              </div>
              <div className="flex flex-none items-center gap-1">
                {!ch.is_preferred && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => preferMut.mutate(ch)}
                    loading={preferMut.isPending && preferMut.variables?.id === ch.id}
                    disabled={preferMut.isPending && preferMut.variables?.id === ch.id}
                  >
                    Set preferred
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove ${KIND_LABEL[ch.kind]} ${ch.value}`}
                  onClick={() => onDelete(ch)}
                  disabled={delMut.isPending}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={onAdd} className="flex flex-wrap items-end gap-2">
        <Select
          label="Kind"
          options={KIND_OPTIONS}
          value={kind}
          onChange={(e) => setKind((e.target as HTMLSelectElement).value as Kind)}
        />
        <div className="flex-1">
          <Input
            label="Value"
            type="text"
            value={value}
            onChange={(e) => setValue((e.target as HTMLInputElement).value)}
            placeholder="e.g. +381 64 123 4567"
          />
        </div>
        <div className="flex-1">
          <Input
            label="Label (optional)"
            type="text"
            value={label}
            onChange={(e) => setLabel((e.target as HTMLInputElement).value)}
            placeholder="e.g. mobile"
          />
        </div>
        <div className="pb-2">
          <Checkbox checked={preferred} onChange={(e) => setPreferred((e.target as HTMLInputElement).checked)} label="Preferred" />
        </div>
        <Button type="submit" variant="secondary" size="sm" loading={addMut.isPending} disabled={addMut.isPending}>
          Add
        </Button>
      </form>
      {formError && <p className="mt-2 text-sm text-destructive">{formError}</p>}
    </section>
  );
}
